import React, { useState } from "react";
import { ShieldOff, RefreshCw, LogOut, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Loading } from "./ui/loading";
import { Button } from "./ui/button";
import escolarAiLogo from "../assets/escolarai-marca.svg";

interface EscolaPermissaoGuardProps {
  children: React.ReactNode;
}

function NoPermissionsScreen() {
  const { user, logout, refreshPermissions } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await refreshPermissions();
    } finally {
      setRefreshing(false);
    }
  }

  const firstName = user?.pessoa?.nome?.split(" ")[0] ?? "Usuário";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header com logo */}
      <header className="px-6 py-5 border-b border-border/40">
        <img src={escolarAiLogo} alt="EscolarAI" className="h-7 w-auto opacity-80" />
      </header>

      {/* Conteúdo central */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">

          {/* Ícone */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
              <ShieldOff className="w-9 h-9 text-amber-500" />
            </div>
          </div>

          {/* Título e descrição */}
          <div className="text-center mb-8 space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              Acesso sem permissão
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Olá, <span className="font-medium text-foreground">{firstName}</span>. Sua conta
              está ativa, mas você não possui nenhum vínculo habilitado para
              acessar o sistema.
            </p>
          </div>

          {/* Card informativo */}
          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6 space-y-3">
            <p className="text-sm font-medium text-foreground">O que pode ter acontecido?</p>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                Seu acesso ainda não foi configurado pelo administrador
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                Seus vínculos foram desativados ou removidos
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                Suas permissões foram concedidas mas ainda não carregaram
              </li>
            </ul>
          </div>

          {/* Ações principais */}
          <div className="flex flex-col gap-3">
            <Button
              className="w-full"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Verificando permissões..." : "Verificar permissões novamente"}
            </Button>

            <Button variant="outline" className="w-full" onClick={logout}>
              <LogOut className="w-4 h-4" />
              Sair da conta
            </Button>
          </div>

          {/* Contato — expansível */}
          <div className="mt-6 border-t border-border pt-4">
            <button
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
              onClick={() => setShowDetails((v) => !v)}
            >
              <Mail className="w-3.5 h-3.5" />
              <span>Precisa de ajuda?</span>
              {showDetails ? (
                <ChevronUp className="w-3.5 h-3.5 ml-auto" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 ml-auto" />
              )}
            </button>

            {showDetails && (
              <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Entre em contato com o administrador do sistema informando seu{" "}
                <span className="font-medium text-foreground">
                  e-mail ({user?.email ?? "não informado"})
                </span>{" "}
                e solicite a configuração dos seus vínculos de acesso.
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export function EscolaPermissaoGuard({ children }: EscolaPermissaoGuardProps) {
  const {
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    getActiveVinculos,
  } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" message="Carregando..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  const hasActiveVinculos = getActiveVinculos().length > 0;

  if (!hasActiveVinculos) {
    return <NoPermissionsScreen />;
  }

  return <>{children}</>;
}
